// QuickActions.js - Header action buttons for Asset Details Redux (PRD-43)
// Quick Scan dropdown, View Inventory, Drift Events, Export dropdown

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './QuickActions.css';

/**
 * @param {object} props
 * @param {string} props.assetId
 * @param {object} props.asset - Asset entity (used to detect IP vs domain and disable deep scan when no discovery)
 * @param {function} props.onScan - Async handler called with scanType string
 * @param {function} [props.onExport] - Optional handler(format) - 'pdf' | 'csv' | 'json'
 * @param {boolean} [props.scanInProgress] - Disable Quick Scan while scanning
 */
function QuickActions({ assetId, asset = {}, onScan, onExport, scanInProgress = false }) {
    const navigate = useNavigate();
    const [scanOpen, setScanOpen] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const scanRef = useRef(null);
    const exportRef = useRef(null);

    // Close menus on outside click
    useEffect(() => {
        const handler = (e) => {
            if (scanRef.current && !scanRef.current.contains(e.target)) setScanOpen(false);
            if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const hasDiscovery = Boolean(asset.lastDiscoveryAt);

    const triggerScan = async (scanType) => {
        setScanOpen(false);
        if (typeof onScan === 'function') {
            await onScan(scanType);
        }
    };

    const handleExport = (format) => {
        setExportOpen(false);
        if (typeof onExport === 'function') {
            onExport(format);
        }
    };

    return (
        <div className="quick-actions">
            {/* Quick Scan dropdown */}
            <div className="quick-actions__dropdown" ref={scanRef}>
                <button
                    type="button"
                    className="quick-actions__btn quick-actions__btn--primary"
                    onClick={() => setScanOpen((v) => !v)}
                    disabled={scanInProgress}
                    aria-haspopup="menu"
                    aria-expanded={scanOpen}
                >
                    {scanInProgress ? 'Scanning…' : 'Quick Scan'}
                    <span className="quick-actions__caret" aria-hidden="true">▾</span>
                </button>
                {scanOpen && (
                    <div className="quick-actions__menu" role="menu">
                        <button
                            type="button"
                            role="menuitem"
                            className="quick-actions__menu-item"
                            onClick={() => triggerScan('discovery')}
                        >
                            <span className="quick-actions__menu-title">Discovery</span>
                            <span className="quick-actions__menu-desc">Subdomain + port enumeration</span>
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            className="quick-actions__menu-item"
                            onClick={() => triggerScan('vuln-scan')}
                            disabled={!hasDiscovery}
                            title={!hasDiscovery ? 'Run discovery first' : undefined}
                        >
                            <span className="quick-actions__menu-title">Vuln Scan</span>
                            <span className="quick-actions__menu-desc">CVE + template-based checks</span>
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            className="quick-actions__menu-item"
                            onClick={() => triggerScan('deep-security')}
                            disabled={!hasDiscovery}
                            title={!hasDiscovery ? 'Run discovery first' : undefined}
                        >
                            <span className="quick-actions__menu-title">Deep Security</span>
                            <span className="quick-actions__menu-desc">SQLi, TestSSL, Nikto (full coverage)</span>
                        </button>
                    </div>
                )}
            </div>

            {/* View Inventory */}
            <button
                type="button"
                className="quick-actions__btn"
                onClick={() => navigate(`/inventory?asset=${encodeURIComponent(assetId)}`)}
            >
                View Inventory
            </button>

            {/* Drift Events */}
            <button
                type="button"
                className="quick-actions__btn"
                onClick={() => navigate(`/drift-alarms?assetId=${encodeURIComponent(assetId)}`)}
            >
                Drift Alarms
            </button>

            {/* Export dropdown — only rendered when an onExport handler is provided.
                Hidden on the production Asset Details page until the export feature
                (PDF/CSV/JSON) is actually built; re-enable by passing onExport. */}
            {typeof onExport === 'function' && (
                <div className="quick-actions__dropdown" ref={exportRef}>
                    <button
                        type="button"
                        className="quick-actions__btn"
                        onClick={() => setExportOpen((v) => !v)}
                        aria-haspopup="menu"
                        aria-expanded={exportOpen}
                    >
                        Export
                        <span className="quick-actions__caret" aria-hidden="true">▾</span>
                    </button>
                    {exportOpen && (
                        <div className="quick-actions__menu" role="menu">
                            <button
                                type="button"
                                role="menuitem"
                                className="quick-actions__menu-item"
                                onClick={() => handleExport('pdf')}
                            >
                                <span className="quick-actions__menu-title">PDF Report</span>
                                <span className="quick-actions__menu-desc">Executive-ready summary</span>
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                className="quick-actions__menu-item"
                                onClick={() => handleExport('csv')}
                            >
                                <span className="quick-actions__menu-title">CSV Findings</span>
                                <span className="quick-actions__menu-desc">Flat list for spreadsheets</span>
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                className="quick-actions__menu-item"
                                onClick={() => handleExport('json')}
                            >
                                <span className="quick-actions__menu-title">JSON (Raw)</span>
                                <span className="quick-actions__menu-desc">Complete scan blob</span>
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default QuickActions;
